import { z } from 'zod';
import { AuditSchema, IdSchema } from './common.ts';

/**
 * Company reference data: the words a company files things under.
 *
 * Company-level rather than per job, because the point of a tag is to find
 * everything of a kind across jobs — "warranty", "weather delay", "change
 * requested". A per-job tag would make the one query it exists for
 * impossible.
 *
 * Shared across modules rather than one list per module. The alternative is
 * four lists that all end up containing "Weather", and a person tagging a
 * daily log and a to-do about the same morning having to pick from two
 * different sets.
 */
export const TAG_COLOURS = ['slate', 'blue', 'green', 'amber', 'red', 'purple', 'teal'] as const;
export const TagColourSchema = z.enum(TAG_COLOURS);
export type TagColour = z.infer<typeof TagColourSchema>;

export const TagSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  name: z.string().trim().min(1).max(60),
  colour: TagColourSchema.default('slate'),
});
export type Tag = z.infer<typeof TagSchema>;

/**
 * Two tags are the same tag if their names match once case and spacing are
 * set aside.
 *
 * "Weather" and "weather " are the same word to everybody but a database,
 * and a company whose tag list holds both has a tag list nobody trusts.
 */
export function tagKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Tags by id, for rendering a list of ids as names. */
export function tagsByIdFrom(tags: readonly Tag[]): ReadonlyMap<string, Tag> {
  return new Map(tags.map((tag) => [tag.id, tag] as const));
}

/** The names behind a list of ids, in the order the ids were given. */
export function tagNames(tags: readonly Tag[], ids: readonly string[]): string[] {
  const byId = tagsByIdFrom(tags);
  // An id with no tag behind it is skipped rather than shown raw: a deleted
  // tag should disappear from the records that used it, not leave a ULID on
  // screen.
  return ids.flatMap((id) => {
    const tag = byId.get(id);
    return tag ? [tag.name] : [];
  });
}
